import React, { useState } from 'react';
import { LanguageCode, SiteSettings } from '../types';
import { translations } from '../i18n/translations';
import { MapPin, Phone, Mail, MessageSquare, Send, CheckCircle2, Globe, Loader2 } from 'lucide-react';
import { sendContactMessageApi } from '../services/api';

interface ContactViewProps {
  currentLang: LanguageCode;
  settings: SiteSettings;
}

export const ContactView: React.FC<ContactViewProps> = ({ currentLang, settings }) => {
  const t = translations[currentLang];
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      await sendContactMessageApi({
        fullName: formData.name,
        email: formData.email,
        subject: formData.subject,
        message: formData.message,
      });
      setSubmitted(true);
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (err: any) {
      setError(err.message || 'Mesaj gönderilirken hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-10 pb-12">
      {/* Header */}
      <div className="text-center max-w-3xl mx-auto space-y-3">
        <span className="px-3.5 py-1 rounded-full bg-[#132238] text-[#38BDF8] text-xs font-extrabold uppercase tracking-wider border border-[#38BDF8]/40 shadow-[0_0_15px_rgba(56,189,248,0.2)]">
          {t.contactBadge}
        </span>
        <h1 className="text-3xl sm:text-5xl font-extrabold text-white font-heading">
          {t.contactMainTitle}
        </h1>
        <p className="text-sm text-gray-300">
          {t.contactSubtitle}
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Contact Info Cards */}
        <div className="space-y-4">
          <div className="bg-[#132238] rounded-3xl p-6 border-2 border-[#38BDF8]/30 space-y-3 shadow-xl">
            <div className="w-10 h-10 rounded-xl bg-[#0B1426] text-[#38BDF8] border border-[#38BDF8]/40 flex items-center justify-center shadow-md">
              <MapPin className="w-5 h-5" />
            </div>
            <h3 className="text-base font-bold text-white">{t.addressTitle}</h3>
            <p className="text-xs text-gray-300 leading-relaxed">{settings.address}</p>
          </div>

          <div className="bg-[#132238] rounded-3xl p-6 border-2 border-[#38BDF8]/30 space-y-3 shadow-xl">
            <div className="w-10 h-10 rounded-xl bg-[#0B1426] text-[#38BDF8] border border-[#38BDF8]/40 flex items-center justify-center shadow-md">
              <Phone className="w-5 h-5" />
            </div>
            <h3 className="text-base font-bold text-white">{t.phoneTitle}</h3>
            <p className="text-xs text-gray-300">{settings.phone}</p>
            <a
              href={`https://wa.me/${settings.whatsapp.replace(/[^0-9]/g, '')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-xl bg-emerald-600/30 text-emerald-300 border border-emerald-500/50 text-xs font-extrabold hover:bg-emerald-600 hover:text-white transition-all mt-1 shadow-md"
            >
              <MessageSquare className="w-3.5 h-3.5" />
              <span>{t.whatsappChat}</span>
            </a>
          </div>

          <div className="bg-[#132238] rounded-3xl p-6 border-2 border-[#38BDF8]/30 space-y-3 shadow-xl">
            <div className="w-10 h-10 rounded-xl bg-[#0B1426] text-[#38BDF8] border border-[#38BDF8]/40 flex items-center justify-center shadow-md">
              <Mail className="w-5 h-5" />
            </div>
            <h3 className="text-base font-bold text-white">{t.emailTitle}</h3>
            <div className="text-xs text-gray-300 space-y-1">
              <p><a href={`mailto:${settings.email}`} className="text-[#38BDF8] font-bold hover:underline">{settings.email}</a></p>
            </div>
          </div>
        </div>

        {/* Form and Map */}
        <div className="lg:col-span-2 space-y-6">
          {/* Form */}
          <div className="bg-[#132238] rounded-3xl p-6 sm:p-8 border-2 border-[#38BDF8]/30 shadow-2xl">
            {submitted ? (
              <div className="p-8 text-center space-y-4">
                <CheckCircle2 className="w-12 h-12 text-[#38BDF8] mx-auto" />
                <h3 className="text-xl font-bold text-white">{t.messageReceived}</h3>
                <p className="text-xs text-gray-300">
                  {t.messageReceivedDesc}
                </p>
                <button
                  onClick={() => setSubmitted(false)}
                  className="px-6 py-2.5 rounded-xl bg-[#38BDF8] text-[#0B1426] font-extrabold text-xs hover:bg-[#A5F3FC]"
                >
                  {t.sendNewMessage}
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <h3 className="text-lg font-bold text-white font-heading mb-2">{t.sendMessageTitle}</h3>

                {error && (
                  <div className="p-3 rounded-xl bg-red-950/80 border border-red-500/50 text-red-200 text-xs">
                    {error}
                  </div>
                )}
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs text-gray-300 font-medium block mb-1">{t.fullNameLabel}</label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Ad Soyad"
                      className="w-full p-3 rounded-xl bg-[#0B1426] border border-[#38BDF8]/30 text-white text-xs focus:outline-none focus:border-[#38BDF8]"
                    />
                  </div>

                  <div>
                    <label className="text-xs text-gray-300 font-medium block mb-1">{t.emailAddressLabel}</label>
                    <input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="E-Posta Adresi"
                      className="w-full p-3 rounded-xl bg-[#0B1426] border border-[#38BDF8]/30 text-white text-xs focus:outline-none focus:border-[#38BDF8]"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-xs text-gray-300 font-medium block mb-1">{t.subjectLabel}</label>
                  <input
                    type="text"
                    required
                    value={formData.subject}
                    onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                    placeholder="Konu"
                    className="w-full p-3 rounded-xl bg-[#0B1426] border border-[#38BDF8]/30 text-white text-xs focus:outline-none focus:border-[#38BDF8]"
                  />
                </div>

                <div>
                  <label className="text-xs text-gray-300 font-medium block mb-1">{t.messageLabel}</label>
                  <textarea
                    required
                    rows={4}
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    placeholder="Mesajınız..."
                    className="w-full p-3 rounded-xl bg-[#0B1426] border border-[#38BDF8]/30 text-white text-xs focus:outline-none focus:border-[#38BDF8]"
                  />
                </div>

                <div className="pt-2 flex justify-end">
                  <button
                    type="submit"
                    disabled={loading}
                    className="px-6 py-3 rounded-2xl bg-[#38BDF8] text-[#0B1426] font-extrabold text-xs hover:bg-[#A5F3FC] hover:shadow-[0_0_20px_rgba(165,243,252,0.8)] transition-all flex items-center gap-2 shadow-md disabled:opacity-50"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span>İletiliyor...</span>
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4" />
                        <span>{t.send}</span>
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>

          {/* Interactive Google Map View */}
          <div className="bg-[#132238] rounded-3xl p-4 sm:p-6 border-2 border-[#38BDF8]/30 space-y-4 shadow-xl">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-[#38BDF8]/20 pb-3">
              <div>
                <h4 className="text-base font-extrabold text-white font-heading flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-[#38BDF8]" />
                  <span>{t.ourLocation}</span>
                </h4>
                <p className="text-xs text-cyan-200/80 mt-0.5">{settings.address}</p>
              </div>
              <a
                href={`https://maps.google.com/?q=${encodeURIComponent(settings.address)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="px-4 py-2 rounded-xl bg-[#0B1426] border border-[#38BDF8] text-[#38BDF8] text-xs font-extrabold hover:bg-[#38BDF8] hover:text-[#0B1426] transition-all self-start sm:self-auto shrink-0 shadow-md flex items-center gap-1.5"
              >
                <Globe className="w-4 h-4" />
                <span>Google Maps</span>
              </a>
            </div>
            <div className="h-60 rounded-2xl overflow-hidden border border-[#38BDF8]/30 relative shadow-inner">
              <iframe
                title="Google Maps Location"
                src={`https://www.google.com/maps?q=${encodeURIComponent(settings.address)}&output=embed`}
                className="w-full h-full border-0"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
